<template>
    <ManagePageContainer>
        <ManageSidebar />
        <MainContentWrapper>
            <!-- * 헤더 섹션 -->
            <HeaderSection>
                <HeaderTitle>
                    <h1>고객 관리</h1>
                    <span>등록된 고객 정보를 관리합니다</span>
                </HeaderTitle>
                <HeaderActions>
                    <PrimaryButton @click="openBulkRegistrationModal">
                        <ListBoxOutline :size="24" :style="{ paddingTop: '3px' }" />고객 일괄등록
                    </PrimaryButton>
                    <PrimaryButton @click="openRegistrationModal">
                        <Plus :size="24" :style="{ paddingTop: '3px' }" />고객 등록
                    </PrimaryButton>
                </HeaderActions>
            </HeaderSection>

            <!-- * 검색 및 필터 섹션 -->
            <SearchSection>
                <SearchCard>
                    <SearchRow>
                        <SearchGroup>
                            <label>검색</label>
                            <SearchInput type="text" v-model="searchQuery" placeholder="고객명 또는 인정번호를 입력하세요"
                                @keyup.enter="searchHandler" />
                            <SearchButton @click="searchHandler">
                                <Magnify :size="20" />
                            </SearchButton>
                        </SearchGroup>
                    </SearchRow>

                    <SearchRow>
                        <FilterGroup>
                            <label>대상</label>
                            <FilterChips>
                                <FilterChip v-for="type in benefitTypes" :key="type.value"
                                    :active="selectedBenefitType === type.value"
                                    @click="filterByBenefitType(type.value)">
                                    {{ type.label }}
                                </FilterChip>
                            </FilterChips>
                        </FilterGroup>
                    </SearchRow>

                    <!-- * 활성 필터 표시 -->
                    <SearchRow v-if="hasActiveFilters">
                        <FilterGroup>
                            <label>활성 필터</label>
                            <ActiveFilterChips>
                                <ActiveFilterChip v-if="selectedBenefitType !== 'all'"
                                    @click="clearFilter('benefitType')">
                                    {{ getBenefitTypeLabel(selectedBenefitType) }} ✕
                                </ActiveFilterChip>
                                <ClearAllFiltersButton @click="clearAllFilters">
                                    모든 필터 초기화
                                </ClearAllFiltersButton>
                            </ActiveFilterChips>
                        </FilterGroup>
                    </SearchRow>
                </SearchCard>
            </SearchSection>

            <!-- * 급여 통계 카드 -->
            <StatsSection>
                <StatsCard>
                    <StatsNumber type="total">{{ clients_info.total_all_cnt }}</StatsNumber>
                    <StatsLabel>전체 수급자</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber type="general">{{ generalClientCount }}</StatsNumber>
                    <StatsLabel>일반 수급자</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber type="basic">{{ basicClientCount }}</StatsNumber>
                    <StatsLabel>기초 수급자</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber type="discount">{{ discountClientCount }}</StatsNumber>
                    <StatsLabel>감경 수급자</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber type="medical">{{ medicalClientCount }}</StatsNumber>
                    <StatsLabel>의료급여 수급자</StatsLabel>
                </StatsCard>
            </StatsSection>

            <!-- * 비급여 통계 카드 -->
            <StatsSection>
                <StatsCard>
                    <StatsNumber type="total">{{ totalNonBenefitCount }}</StatsNumber>
                    <StatsLabel>전체 고객</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber type="general">{{ nonBenefitGeneralCount }}</StatsNumber>
                    <StatsLabel>일반 고객</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber type="basic">{{ nonBenefitOxygenCount }}</StatsNumber>
                    <StatsLabel>산소 고객</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber type="discount">{{ nonBenefitDiabetesCount }}</StatsNumber>
                    <StatsLabel>당뇨 고객</StatsLabel>
                </StatsCard>
                <StatsCard>
                    <StatsNumber type="medical">{{ nonBenefitAssistiveDeviceCount }}</StatsNumber>
                    <StatsLabel>보장구 고객</StatsLabel>
                </StatsCard>
            </StatsSection>

            <!-- * 고객 목록 테이블 -->
            <TableSection>
                <TableHeader>
                    <SubTitle>고객 목록</SubTitle>
                    <ItemsPerPageSelector>
                        <select v-model="selectedTypeFilter" @change="changeTypeFilter">
                            <option value="전체">전체</option>
                            <option value="급여">급여</option>
                            <option value="비급여">비급여</option>
                        </select>
                    </ItemsPerPageSelector>
                    <ItemsPerPageSelector>
                        <select v-model="itemsPerPage" @change="changeItemsPerPage">
                            <option value="10">10개씩 보기</option>
                            <option value="20">20개씩 보기</option>
                            <option value="50">50개씩 보기</option>
                            <option value="100">100개씩 보기</option>
                        </select>
                    </ItemsPerPageSelector>
                </TableHeader>

                <!-- * 테이블 뷰 -->
                <TableContainer>
                    <ModernTable>
                        <thead>
                            <tr>
                                <th>고객명</th>
                                <th>인정번호</th>
                                <th>기관기호</th>
                                <th>대상</th>
                                <th class="address">주소</th>
                                <th>보호자명</th>
                                <th>관계</th>
                                <th>담당자</th>
                                <th>연락처</th>
                                <th>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="client in paginatedClients" :key="client.number" :class="{
                                'success': client.target === '일반',
                                'info': client.target === '의료급여',
                                'danger': client.target === '기초',
                                'warning': client.target === '감경',
                                'client_tr': true,
                                'benefit-row': client.type === '급여',
                                'non-benefit-row': client.type === '비급여',
                                'blurred': isTestUser
                            }" @click="handleClientRowClick(client)" style="cursor: pointer;">
                                <td>
                                    <InfoText>
                                        <Name>{{ client.name }}</Name>
                                    </InfoText>
                                </td>
                                <td>{{ client.number }}</td>
                                <td>{{ client.cnum }}</td>
                                <td>
                                    <BenefitTypeChip
                                        :type="client.type === '급여' ? client.target : client.customer_type">
                                        {{ client.type === '급여' ? client.target : client.customer_type }}
                                    </BenefitTypeChip>
                                </td>
                                <td class="address">{{ client.address }}</td>
                                <td>{{ client.guardname }}</td>
                                <td>{{ client.guardtarget }}</td>
                                <td>{{ client.manager }}</td>
                                <td>
                                    <ContactInfo>
                                        <div>{{ client.phone1 }}</div>
                                        <div v-if="client.phone2 && client.phone2 !== client.phone1">{{ client.phone2 }}
                                        </div>
                                    </ContactInfo>
                                </td>
                                <td>
                                    <button @click.stop="handleDeleteClient(client.id)"
                                        style="background: none; border: none; cursor: pointer;">
                                        <TrashCan :size="20" :style="{ color: '#dc2626' }" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </ModernTable>
                </TableContainer>

                <!-- * 데이터 없음 표시 -->
                <EmptyState v-if="clients_info.clients.length === 0 && !isLoading">
                    <EmptyStateIcon>👥</EmptyStateIcon>
                    <EmptyStateTitle>등록된 고객이 없습니다</EmptyStateTitle>
                    <EmptyStateText v-if="searchQuery || selectedBenefitType !== 'all'">
                        검색 조건을 변경하거나 필터를 초기화해보세요.
                    </EmptyStateText>
                    <EmptyStateText v-else>
                        등록된 고객이 없습니다.
                    </EmptyStateText>
                </EmptyState>

                <!-- * 페이지네이션 -->
                <PaginationContainer v-if="totalPages > 1">
                    <Pagination>
                        <PaginationButton :disabled="currentPage === 1" @click="goToPage(1)">
                            <ChevronDoubleLeft :size="20" />
                        </PaginationButton>
                        <PaginationButton :disabled="currentPage <= 10" @click="goToPageGroup('prev')">
                            <ChevronLeft :size="20" />
                        </PaginationButton>

                        <PaginationNumber v-for="page in displayedPages" :key="page" :active="page === currentPage"
                            @click="goToPage(page)">
                            {{ page }}
                        </PaginationNumber>

                        <PaginationButton :disabled="currentPage > Math.floor(totalPages / 10) * 10"
                            @click="goToPageGroup('next')">
                            <ChevronRight :size="20" />
                        </PaginationButton>
                        <PaginationButton :disabled="currentPage === totalPages" @click="goToPage(totalPages)">
                            <ChevronDoubleRight :size="20" />
                        </PaginationButton>
                    </Pagination>
                </PaginationContainer>
            </TableSection>
        </MainContentWrapper>
    </ManagePageContainer>

    <!-- ! 고객 등록 모달 -->
    <RegisterRecipientsModal v-if="showRegistrationModal" ref="registerRecipientsModal" :isTestUser="isTestUser"
        @registration-completed="handleRegistrationCompleted" @close="closeRegistrationModal" />
</template>

<script>
import { reactive } from 'vue';
import ManageSidebar from './ManageSidebar.vue';
import RegisterRecipientsModal from './modal/RegisterRecipientsModal.vue';
import * as ManageClientCss from '@/assets/styles/manage/ManageClientCss.js';

import ChevronDoubleLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import ChevronDoubleRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import Magnify from 'vue-material-design-icons/Magnify.vue';
import Plus from 'vue-material-design-icons/Plus.vue';
import ListBoxOutline from 'vue-material-design-icons/ListBoxOutline.vue';
import TrashCan from 'vue-material-design-icons/TrashCan.vue';

export default {
    name: 'ManageClient',

    components: {
        ManageSidebar,
        RegisterRecipientsModal,
        ...ManageClientCss,
        ChevronDoubleLeft,
        ChevronDoubleRight,
        ChevronLeft,
        ChevronRight,
        Magnify,
        Plus,
        ListBoxOutline,
        TrashCan
    },

    setup() {
        return {
            clients_info: reactive({
                clients: [],
                total_all_cnt: 0,
                general_cnt: 0,
                basic_cnt: 0,
                discount_cnt: 0,
                medical_cnt: 0,
                non_benefit_cnt: 0,
                non_benefit_general_cnt: 0,
                non_benefit_oxygen_cnt: 0,
                non_benefit_diabetes_cnt: 0,
                non_benefit_assistive_device_cnt: 0
            }),
        };
    },

    data() {
        return {
            // 검색 관련
            searchQuery: '',
            searchSubmitted: '',
            selectedBenefitType: 'all',
            selectedTypeFilter: '전체',
            typeFilterOptions: [
                { value: '전체', label: '전체' },
                { value: '급여', label: '급여' },
                { value: '비급여', label: '비급여' }
            ],

            // 페이징
            currentPage: 1,
            itemsPerPage: 10,
            isLoading: false,

            // 필터 옵션
            benefitTypes: [
                { value: 'all', label: '전체' },
                { value: '일반', label: '일반' },
                { value: '기초', label: '기초' },
                { value: '감경', label: '감경' },
                { value: '의료급여', label: '의료급여' },
                { value: '산소', label: '산소' },
                { value: '당뇨', label: '당뇨' },
                { value: '보장구', label: '보장구' }
            ],

            // 고객 등록
            showRegistrationModal: false,
            showBulkRegistrationModal: false,

            // 고객 상세 모달
            showClientDetailModal: false,
            selectedClient: null,
        }
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        // 활성 필터가 있는지 확인
        hasActiveFilters() {
            return this.selectedBenefitType !== 'all';
        },

        paginatedClients() {
            let filteredClients = this.clients_info.clients;

            // 급여/비급여 필터 적용
            if (this.selectedTypeFilter === '급여') {
                filteredClients = filteredClients.filter(client => client.type === '급여');
            } else if (this.selectedTypeFilter === '비급여') {
                filteredClients = filteredClients.filter(client => client.type === '비급여');
            }
            // '전체'인 경우 모든 데이터 표시

            return filteredClients;
        },

        totalPages() {
            return Math.max(1, Math.ceil(this.clients_info.total_all_cnt / this.itemsPerPage));
        },

        displayedPages() {
            const currentGroup = Math.floor((this.currentPage - 1) / 10);
            const startPage = currentGroup * 10 + 1;
            const endPage = Math.min(startPage + 9, this.totalPages);

            const pages = [];
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            return pages;
        },

        // 통계 계산
        basicClientCount() {
            return this.clients_info.basic_cnt;
        },

        generalClientCount() {
            return this.clients_info.general_cnt;
        },

        discountClientCount() {
            return this.clients_info.discount_cnt;
        },

        medicalClientCount() {
            return this.clients_info.medical_cnt;
        },

        // 비급여 고객 통계 계산
        nonBenefitGeneralCount() {
            return this.clients_info.non_benefit_general_cnt || 0;
        },

        nonBenefitOxygenCount() {
            return this.clients_info.non_benefit_oxygen_cnt || 0;
        },

        nonBenefitDiabetesCount() {
            return this.clients_info.non_benefit_diabetes_cnt || 0;
        },

        nonBenefitAssistiveDeviceCount() {
            return this.clients_info.non_benefit_assistive_device_cnt || 0;
        },

        totalNonBenefitCount() {
            return this.nonBenefitGeneralCount + this.nonBenefitOxygenCount +
                this.nonBenefitDiabetesCount + this.nonBenefitAssistiveDeviceCount;
        },
    },

    methods: {
        handleDeleteClient(clientId) {
            // 고객 정보를 찾아서 삭제 확인 메시지에 표시
            const client = this.clients_info.clients.find(c => c.id === clientId);
            const clientName = client ? client.name : '고객';

            this.$swal.fire({
                title: '고객 삭제',
                text: `"${clientName}" 고객을 삭제하시겠습니까?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: '삭제',
                cancelButtonText: '취소',
                confirmButtonColor: '#dc2626',
                cancelButtonColor: '#6b7280',
                reverseButtons: true,
                focusCancel: true
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await this.deleteRecipient(clientId);
                }
            });
        },

        // 고객 삭제 실행
        async deleteRecipient(recipientId) {
            try {
                // 로딩 표시
                const loading = this.$swal.fire({
                    title: '고객 삭제 중...',
                    text: '잠시만 기다려주세요.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                // 백엔드 API 호출
                const response = await this.$axios({
                    method: 'delete',
                    url: `${this.$apiUrls.hanqmedi}/deleteRecipient`,
                    data: {
                        recipientId: recipientId,
                        companykey: this.$globalState.company_key
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                });

                loading.close();

                if (response.data && response.data.success) {
                    // 성공 메시지
                    await this.$swal.fire({
                        title: '삭제 완료',
                        text: response.data.message || '고객이 성공적으로 삭제되었습니다.',
                        icon: 'success',
                        confirmButtonText: '확인',
                        timer: 2000,
                        showConfirmButton: true
                    });

                    // 데이터 새로고침
                    await this.fetchData();
                } else {
                    throw new Error(response.data?.message || '고객 삭제에 실패했습니다.');
                }

            } catch (error) {
                console.error('고객 삭제 오류:', error);

                let errorMessage = '고객 삭제 중 오류가 발생했습니다.';
                let errorTitle = '삭제 실패';

                // 구체적인 에러 메시지 처리
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                // HTTP 상태 코드에 따른 에러 제목 설정
                if (error.response?.status === 400) {
                    errorTitle = '입력 오류';
                } else if (error.response?.status === 401) {
                    errorTitle = '인증 오류';
                } else if (error.response?.status === 403) {
                    errorTitle = '권한 오류';
                } else if (error.response?.status === 404) {
                    errorTitle = '고객을 찾을 수 없음';
                } else if (error.response?.status === 500) {
                    errorTitle = '서버 오류';
                }

                // 실패 메시지 표시
                this.$swal.fire({
                    title: errorTitle,
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인',
                    showCloseButton: true
                });
            }
        },

        // 고객 행 클릭 처리
        handleClientRowClick(client) {
            if (client.type === '급여') {
                this.$swal.fire({
                    title: '급여 고객',
                    text: '해당 고객은 급여 고객입니다. 비급여만 수정 가능합니다.',
                    icon: 'info',
                    confirmButtonText: '확인'
                });
                return;
            }

            // 비급여 고객인 경우에만 모달 열기
            this.openClientDetailModal(client);
        },

        // 고객 상세 모달 열기
        openClientDetailModal(client) {
            this.selectedClient = client;
            this.showClientDetailSwal(client);
        },

        // 고객 상세 정보 모달
        async showClientDetailSwal(client) {
            try {
                const { value: formValues } = await this.$swal.fire({
                    title: '고객 정보 수정',
                    html: this.getClientDetailFormHTML(client),
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: '수정',
                    cancelButtonText: '닫기',
                    width: '600px',
                    allowOutsideClick: false,
                    preConfirm: () => {
                        return this.validateClientDetailForm();
                    },
                    didOpen: () => {
                        this.initializeClientDetailForm(client);
                    }
                });

                if (formValues) {
                    await this.updateClient(formValues);
                }
            } catch (error) {
                console.error('고객 상세 모달 오류:', error);
                this.$swal.fire({
                    title: '오류 발생',
                    text: '고객 정보를 불러오는 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 고객 상세 정보 폼 HTML
        getClientDetailFormHTML(client) {
            return `
        <div class="client-detail-form">
            <style>
                .client-detail-form {
                    max-height: 600px;
                    overflow-y: auto;
                    padding: 15px;
                }
                
                .customer-type-tabs {
                    display: flex;
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 4px;
                    margin-bottom: 24px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }
                
                .customer-type-tab {
                    flex: 1;
                    padding: 12px 16px;
                    text-align: center;
                    font-size: 14px;
                    font-weight: 600;
                    color: #64748b;
                    background: transparent;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                }
                
                .customer-type-tab:hover {
                    background: rgba(15, 76, 92, 0.05);
                }
                
                /* 일반 탭 */
                .customer-type-tab[data-type="일반"]:hover {
                    color: #0d6efd;
                    background: rgba(13, 110, 253, 0.05);
                }
                
                .customer-type-tab[data-type="일반"].active {
                    color: #ffffff;
                    background: linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%);
                    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
                }
                
                .customer-type-tab[data-type="일반"].active::before {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 6px solid #0b5ed7;
                }
                
                /* 산소 탭 */
                .customer-type-tab[data-type="산소"]:hover {
                    color: #dc3545;
                    background: rgba(220, 53, 69, 0.05);
                }
                
                .customer-type-tab[data-type="산소"].active {
                    color: #ffffff;
                    background: linear-gradient(135deg, #dc3545 0%, #b02a37 100%);
                    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
                }
                
                .customer-type-tab[data-type="산소"].active::before {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 6px solid #b02a37;
                }
                
                /* 당뇨 탭 */
                .customer-type-tab[data-type="당뇨"]:hover {
                    color: rgb(21, 142, 163);
                    background: rgba(21, 142, 163, 0.05);
                }
                
                .customer-type-tab[data-type="당뇨"].active {
                    color: #ffffff;
                    background: linear-gradient(135deg, rgb(21, 142, 163) 0%, rgb(17, 114, 131) 100%);
                    box-shadow: 0 4px 12px rgba(21, 142, 163, 0.3);
                }
                
                .customer-type-tab[data-type="당뇨"].active::before {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 6px solid rgb(17, 114, 131);
                }
                
                /* 보장구 탭 */
                .customer-type-tab[data-type="보장구"]:hover {
                    color: #20c997;
                    background: rgba(32, 201, 151, 0.05);
                }
                
                .customer-type-tab[data-type="보장구"].active {
                    color: #ffffff;
                    background: linear-gradient(135deg, #20c997 0%, #1aa179 100%);
                    box-shadow: 0 4px 12px rgba(32, 201, 151, 0.3);
                }
                
                .customer-type-tab[data-type="보장구"].active::before {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 6px solid #1aa179;
                }
                
                .form-row {
                    display: flex;
                    gap: 16px;
                    margin-bottom: 20px;
                }
                
                .form-group {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }
                
                .form-group.full-width {
                    flex: none;
                    width: 100%;
                }
                
                .form-group label {
                    font-size: 15px;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 6px;
                    display: block;
                }
                
                .form-group input,
                .form-group select {
                    padding: 12px 16px;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 14px;
                    transition: all 0.2s ease;
                    background: #ffffff;
                    color: #1f2937;
                }
                
                .form-group input:focus,
                .form-group select:focus {
                    outline: none;
                    border-color: #0f4c5c;
                }
                
                .form-group input::placeholder {
                    color: #9ca3af;
                }
                
                /* 블러 효과를 위한 CSS */
                .blurred {
                    filter: blur(4px) !important;
                    pointer-events: auto;
                    user-select: auto;
                    -webkit-user-select: auto;
                    -moz-user-select: auto;
                    -ms-user-select: auto;
                }
                
                .blurred * {
                    pointer-events: auto;
                    user-select: auto;
                    -webkit-user-select: auto;
                    -moz-user-select: auto;
                    -ms-user-select: auto;
                }
                
                .address-input-group {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }
                
                .address-input-group input {
                    flex: 1;
                }
                
                .address-search-btn {
                    padding: 12px 20px;
                    background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    white-space: nowrap;
                    box-shadow: 0 4px 15px rgba(15, 76, 92, 0.2);
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    min-width: 120px;
                }
                
                .address-search-btn::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                    transition: left 0.5s;
                }
                
                .address-search-btn:hover {
                    background: linear-gradient(135deg, #1a6b7a 0%, #0f4c5c 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(15, 76, 92, 0.3);
                }
                
                .address-search-btn:hover::before {
                    left: 100%;
                }
                
                .address-search-btn:active {
                    transform: translateY(0);
                    box-shadow: 0 2px 10px rgba(15, 76, 92, 0.2);
                }
                
                .address-search-btn:focus {
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(15, 76, 92, 0.1), 0 4px 15px rgba(15, 76, 92, 0.2);
                }
                
                .address-search-btn .search-icon {
                    width: 16px;
                    height: 16px;
                    fill: currentColor;
                }
                
                .form-group input[readonly] {
                    background: #f9fafb;
                    color: #6b7280;
                    cursor: pointer;
                }
                
                .form-group input[readonly]:hover {
                    background: #f3f4f6;
                }
                
                /* 스크롤바 스타일링 */
                .client-detail-form::-webkit-scrollbar {
                    width: 6px;
                }
                
                .client-detail-form::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 3px;
                }
                
                .client-detail-form::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }
                
                .client-detail-form::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
            </style>
            
            <div class="customer-type-tabs">
                <button type="button" class="customer-type-tab" data-type="일반">일반</button>
                <button type="button" class="customer-type-tab" data-type="산소">산소</button>
                <button type="button" class="customer-type-tab" data-type="당뇨">당뇨</button>
                <button type="button" class="customer-type-tab" data-type="보장구">보장구</button>
            </div>
            
            <div class="form-row">
                <div class="form-group">
                    <label for="clientName">고객명</label>
                    <input type="text" id="clientName" placeholder="고객명을 입력하세요" ${this.isTestUser ? 'class="blurred"' : ''}>
                </div>
                <div class="form-group">
                    <label for="clientNumber">인정번호</label>
                    <input type="text" id="clientNumber" placeholder="인정번호를 입력하세요" ${this.isTestUser ? 'class="blurred"' : ''}>
                </div>
            </div>
            
            <div class="form-row">
                <div class="form-group">
                    <label for="clientTarget">구분</label>
                    <select id="clientTarget" ${this.isTestUser ? 'class="blurred"' : ''}>
                        <option value="">구분을 선택하세요</option>
                        <option value="일반">일반</option>
                        <option value="기초">기초</option>
                        <option value="감경">감경</option>
                        <option value="의료급여">의료급여</option>
                    </select>
                </div>
                <div class="form-group">
                    <label for="clientManager">담당자</label>
                    <input type="text" id="clientManager" placeholder="담당자를 입력하세요" ${this.isTestUser ? 'class="blurred"' : ''}>
                </div>
            </div>
            
            <div class="form-row">
                <div class="form-group full-width">
                    <label for="clientAddress">주소1</label>
                    <div class="address-input-group">
                        <input type="text" id="clientAddress" placeholder="주소1을 입력하세요" readonly ${this.isTestUser ? 'class="blurred"' : ''}>
                        <button type="button" id="clientAddressSearchBtn" class="address-search-btn">
                            주소 검색
                        </button>
                    </div>
                    <input type="text" id="clientAddressDetail" placeholder="상세주소1을 입력하세요" style="margin-top: 8px;" ${this.isTestUser ? 'class="blurred"' : ''}>
                </div>
            </div>
            
            <div class="form-row">
                <div class="form-group full-width">
                    <label for="clientAddress2">주소2</label>
                    <div class="address-input-group">
                        <input type="text" id="clientAddress2" placeholder="주소2를 입력하세요" readonly ${this.isTestUser ? 'class="blurred"' : ''}>
                        <button type="button" id="clientAddress2SearchBtn" class="address-search-btn">
                            주소 검색
                        </button>
                    </div>
                    <input type="text" id="clientAddressDetail2" placeholder="상세주소2를 입력하세요" style="margin-top: 8px;" ${this.isTestUser ? 'class="blurred"' : ''}>
                </div>
            </div>
            
            <div class="form-row">
                <div class="form-group">
                    <label for="clientGuardianName">보호자명</label>
                    <input type="text" id="clientGuardianName" placeholder="보호자명을 입력하세요" ${this.isTestUser ? 'class="blurred"' : ''}>
                </div>
                <div class="form-group">
                    <label for="clientGuardianRelation">보호자 관계</label>
                    <select id="clientGuardianRelation" ${this.isTestUser ? 'class="blurred"' : ''}>
                        <option value="">관계를 선택하세요</option>
                        <option value="본인">본인</option>
                        <option value="배우자">배우자</option>
                        <option value="자녀">자녀</option>
                        <option value="부모">부모</option>
                        <option value="기타">기타</option>
                    </select>
                </div>
            </div>
            
            <div class="form-row">
                <div class="form-group">
                    <label for="clientPhone1">연락처1</label>
                    <input type="tel" id="clientPhone1" placeholder="연락처1을 입력하세요" ${this.isTestUser ? 'class="blurred"' : ''}>
                </div>
                <div class="form-group">
                    <label for="clientPhone2">연락처2</label>
                    <input type="tel" id="clientPhone2" placeholder="연락처2를 입력하세요" ${this.isTestUser ? 'class="blurred"' : ''}>
                </div>
            </div>
        </div>
    `;
        },

        // 고객 상세 정보 폼 초기화
        initializeClientDetailForm(client) {
            // 폼 필드에 기존 데이터 설정
            const nameField = document.getElementById('clientName');
            const numberField = document.getElementById('clientNumber');
            const cnumField = document.getElementById('clientCnum');
            const targetField = document.getElementById('clientTarget');
            const addressField = document.getElementById('clientAddress');
            const addressDetailField = document.getElementById('clientAddressDetail');
            const address2Field = document.getElementById('clientAddress2');
            const addressDetail2Field = document.getElementById('clientAddressDetail2');
            const guardianNameField = document.getElementById('clientGuardianName');
            const guardianRelationField = document.getElementById('clientGuardianRelation');
            const managerField = document.getElementById('clientManager');
            const phone1Field = document.getElementById('clientPhone1');
            const phone2Field = document.getElementById('clientPhone2');

            // 각 필드가 존재하는지 확인 후 값 설정
            if (nameField) nameField.value = client.name || '';
            if (numberField) numberField.value = client.number || '';
            if (cnumField) cnumField.value = client.cnum || '';
            if (targetField) targetField.value = client.target || '';
            if (addressField) addressField.value = client.address || '';
            if (address2Field) address2Field.value = client.address2 || '';

            // 상세주소 필드 처리
            if (addressDetailField) {
                const detailAddress = client.address_detail || '';
                addressDetailField.value = detailAddress;
            }

            if (addressDetail2Field) {
                const detailAddress2 = client.address_detail2 || '';
                addressDetail2Field.value = detailAddress2;
            }

            if (guardianNameField) guardianNameField.value = client.guardname || '';
            if (guardianRelationField) guardianRelationField.value = client.guardtarget || '';
            if (managerField) managerField.value = client.manager || '';
            if (phone1Field) phone1Field.value = client.phone1 || '';
            if (phone2Field) phone2Field.value = client.phone2 || '';

            // 고객 유형 탭 초기화
            const tabs = document.querySelectorAll('.customer-type-tab');
            const customerType = client.customer_type || '일반';

            tabs.forEach(tab => {
                tab.classList.remove('active');
                if (tab.getAttribute('data-type') === customerType) {
                    tab.classList.add('active');
                }
            });

            // 고객 유형 탭 이벤트 리스너
            tabs.forEach(tab => {
                tab.addEventListener('click', (e) => {
                    // 모든 탭에서 active 클래스 제거
                    tabs.forEach(t => t.classList.remove('active'));
                    // 클릭된 탭에 active 클래스 추가
                    e.target.classList.add('active');
                });
            });

            // 주소 검색 버튼 이벤트 설정
            const addressSearchBtn = document.getElementById('clientAddressSearchBtn');
            if (addressSearchBtn) {
                addressSearchBtn.addEventListener('click', () => {
                    this.searchClientAddress('clientAddress', 'clientAddressDetail');
                });
            }

            const address2SearchBtn = document.getElementById('clientAddress2SearchBtn');
            if (address2SearchBtn) {
                address2SearchBtn.addEventListener('click', () => {
                    this.searchClientAddress('clientAddress2', 'clientAddressDetail2');
                });
            }
        },

        // 고객 상세 정보 폼 검증
        validateClientDetailForm() {
            const nameField = document.getElementById('clientName');
            const managerField = document.getElementById('clientManager');

            if (!nameField || !managerField) {
                this.$swal.showValidationMessage('폼 필드를 찾을 수 없습니다.');
                return false;
            }

            const name = nameField.value.trim();
            const manager = managerField.value.trim();

            // 선택된 고객 유형 가져오기
            const activeTab = document.querySelector('.customer-type-tab.active');
            const customerType = activeTab ? activeTab.getAttribute('data-type') : '일반';

            if (!name) {
                this.$swal.showValidationMessage('고객명을 입력해주세요.');
                return false;
            }

            if (!manager) {
                this.$swal.showValidationMessage('담당자를 입력해주세요.');
                return false;
            }

            return {
                name,
                manager,
                customerType
            };
        },

        // 고객 정보 수정
        async updateClient(formValues) {
            try {
                // 폼 필드들을 안전하게 가져오기
                const nameField = document.getElementById('clientName');
                const numberField = document.getElementById('clientNumber');
                const cnumField = document.getElementById('clientCnum');
                const targetField = document.getElementById('clientTarget');
                const addressField = document.getElementById('clientAddress');
                const addressDetailField = document.getElementById('clientAddressDetail');
                const address2Field = document.getElementById('clientAddress2');
                const addressDetail2Field = document.getElementById('clientAddressDetail2');
                const guardianNameField = document.getElementById('clientGuardianName');
                const guardianRelationField = document.getElementById('clientGuardianRelation');
                const managerField = document.getElementById('clientManager');
                const phone1Field = document.getElementById('clientPhone1');
                const phone2Field = document.getElementById('clientPhone2');

                // 각 필드가 존재하는지 확인
                if (!nameField || !targetField || !addressField || !managerField) {
                    throw new Error('필수 폼 필드를 찾을 수 없습니다.');
                }

                const clientId = this.selectedClient.id || this.selectedClient.num;

                if (!clientId) {
                    throw new Error('고객 ID를 찾을 수 없습니다.');
                }

                // 선택된 고객 유형 가져오기
                const activeTab = document.querySelector('.customer-type-tab.active');
                const customerType = activeTab ? activeTab.getAttribute('data-type') : '일반';

                // 폼 데이터 수집
                const updateData = {
                    num: clientId,
                    name: nameField.value.trim(),
                    number: numberField ? numberField.value.trim() || null : null,
                    cnum: cnumField ? cnumField.value.trim() || null : null,
                    target: targetField.value || null,
                    customer_type: customerType,
                    address: addressField.value.trim() || null,
                    address_detail: addressDetailField ? addressDetailField.value.trim() || null : null,
                    address2: address2Field ? address2Field.value.trim() || null : null,
                    address_detail2: addressDetail2Field ? addressDetail2Field.value.trim() || null : null,
                    guard_name: guardianNameField ? guardianNameField.value.trim() || null : null,
                    guard_target: guardianRelationField ? guardianRelationField.value.trim() || null : null,
                    manager: managerField.value.trim() || null,
                    phone1: phone1Field ? phone1Field.value.trim() || null : null,
                    phone2: phone2Field ? phone2Field.value.trim() || null : null,
                    companykey: this.$globalState.company_key
                };

                // undefined 값 체크 및 null로 변환
                Object.keys(updateData).forEach(key => {
                    if (updateData[key] === undefined) {
                        updateData[key] = null;
                    }
                });

                // 백엔드 API 호출
                const response = await this.$axios({
                    method: 'put',
                    url: `${this.$apiUrls.hanqmedi}/updateRecipient`,
                    data: updateData,
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data && response.data.success) {
                    // 성공 메시지
                    await this.$swal.fire({
                        title: '수정 완료',
                        text: `"${updateData.name}" 고객 정보가 성공적으로 수정되었습니다.`,
                        icon: 'success',
                        confirmButtonText: '확인'
                    });

                    // 데이터 새로고침
                    await this.fetchData();
                } else {
                    throw new Error(response.data?.message || '고객 정보 수정에 실패했습니다.');
                }

            } catch (error) {
                console.error('고객 정보 수정 오류:', error);

                let errorMessage = '고객 정보 수정 중 오류가 발생했습니다.';
                let errorTitle = '수정 실패';

                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                if (error.response?.status === 400) {
                    errorTitle = '입력 오류';
                } else if (error.response?.status === 401) {
                    errorTitle = '인증 오류';
                } else if (error.response?.status === 500) {
                    errorTitle = '서버 오류';
                }

                this.$swal.fire({
                    title: errorTitle,
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 고객` 주소 검색
        searchClientAddress(addressFieldId, detailFieldId) {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    const addressField = document.getElementById(addressFieldId);
                    if (addressField) {
                        addressField.value = data.address;
                        const addressDetailField = document.getElementById(detailFieldId);
                        if (addressDetailField) {
                            addressDetailField.focus();
                        }
                    }
                }
            }).open();
        },

        openRegistrationModal() {
            this.showRegistrationModal = true;
        },

        closeRegistrationModal() {
            this.showRegistrationModal = false;
        },

        async handleRegistrationCompleted() {
            // 등록 완료 후 데이터 새로고침
            await this.fetchData();
            this.closeRegistrationModal();
        },

        openBulkRegistrationModal() {
            this.showBulkRegistrationModal = true;
            this.showBulkRegistrationSwal();
        },

        getBulkRegistrationHTML() {
            return `
                <div class="bulk-registration-modal">
                    <style>
                        .bulk-registration-modal {
                            text-align: center;
                            padding: 20px 0;
                        }
                        
                        .bulk-description {
                            font-size: 14px;
                            color: #6b7280;
                            margin-bottom: 32px;
                            line-height: 1.5;
                        }
                        
                        .bulk-actions {
                            display: flex;
                            flex-direction: column;
                            gap: 16px;
                            align-items: center;
                        }
                        
                        .bulk-btn {
                            padding: 16px 32px;
                            border: none;
                            border-radius: 16px;
                            font-size: 15px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: all 0.3s ease;
                            display: flex;
                            align-items: center;
                            gap: 12px;
                            min-width: 200px;
                            justify-content: center;
                        }
                        
                        .bulk-btn.primary {
                            background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
                            color: white;
                            box-shadow: 0 4px 15px rgba(15, 76, 92, 0.2);
                        }
                        
                        .bulk-btn.primary:hover {
                            background: linear-gradient(135deg, #1a6b7a 0%, #0f4c5c 100%);
                            transform: translateY(-2px);
                            box-shadow: 0 6px 20px rgba(15, 76, 92, 0.3);
                        }
                        
                        .bulk-btn.secondary {
                            background: #f8fafc;
                            color: #374151;
                            border: 2px solid #e2e8f0;
                        }
                        
                        .bulk-btn.secondary:hover {
                            background: #f1f5f9;
                            border-color: #cbd5e1;
                            transform: translateY(-1px);
                        }
                        
                        .bulk-btn:active {
                            transform: translateY(0);
                        }
                        
                        .bulk-info {
                            margin-top: 24px;
                            padding: 16px;
                            background: #f0f9ff;
                            border-radius: 12px;
                        }
                        
                        .bulk-info h4 {
                            margin: 0 0 8px 0;
                            color: #0369a1;
                            font-size: 14px;
                            font-weight: 600;
                        }
                        
                        .bulk-info p {
                            margin: 0;
                            color: #0c4a6e;
                            font-size: 13px;
                            line-height: 1.4;
                        }
                    </style>
                    
                    <p class="bulk-description">
                        엑셀 템플릿을 다운로드하여 고객 정보를 입력한 후<br>
                        업로드하여 여러 고객을 한 번에 등록할 수 있습니다.
                    </p>
                    
                    <div class="bulk-actions">
                        <button type="button" class="bulk-btn secondary" id="downloadTemplateBtn">
                            📥 템플릿 다운로드
                        </button>
                        <button type="button" class="bulk-btn primary" id="uploadExcelBtn">
                            📤 엑셀 파일 업로드
                        </button>
                    </div>
                    
                    <div class="bulk-info">
                        <h4>📋 템플릿 사용 방법</h4>
                        <p>
                            1. 템플릿을 다운로드하여 고객 정보를 입력하세요<br>
                            2. 엑셀 파일 내 모든 항목을 입력하세요<br>
                            3. 엑셀 파일을 업로드하여 일괄등록을 진행하세요
                        </p>
                    </div>
                </div>
            `;
        },

        async showBulkRegistrationSwal() {
            try {
                const { value: formValues } = await this.$swal.fire({
                    title: '고객 일괄등록',
                    html: this.getBulkRegistrationHTML(),
                    focusConfirm: false,
                    showCancelButton: false,
                    confirmButtonText: '닫기',
                    width: '500px',
                    didOpen: () => {
                        this.initializeBulkRegistrationEvents();
                    }
                });
            } catch (error) {
                console.error('고객 일괄등록 모달 오류:', error);
            }
        },

        initializeBulkRegistrationEvents() {
            // 템플릿 다운로드 버튼
            const downloadBtn = document.getElementById('downloadTemplateBtn');
            if (downloadBtn) {
                downloadBtn.addEventListener('click', this.downloadTemplate);
            }

            // 엑셀 업로드 버튼
            const uploadBtn = document.getElementById('uploadExcelBtn');
            if (uploadBtn) {
                uploadBtn.addEventListener('click', this.uploadExcelFile);
            }
        },

        async downloadTemplate() {
            try {
                // 로딩 표시
                const loading = this.$swal.fire({
                    title: '템플릿 다운로드 중...',
                    text: '잠시만 기다려주세요.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                let response;

                // 일렉트론 환경에서 안정적인 다운로드 처리
                if (this.$env.isElectron) {
                    try {
                        // 방법 1: IPC를 통한 다운로드 (권장)
                        if (window.electronAPI && window.electronAPI.downloadFile) {
                            const result = await window.electronAPI.downloadFile(
                                `${this.$apiUrls.hanqmedi}/downloadBeneficiaryExcelTemplate`,
                                '고객 일괄등록 템플릿.xlsx'
                            );

                            if (result.success) {
                                loading.close();
                                this.$swal.fire({
                                    title: '다운로드 완료',
                                    text: '엑셀 템플릿이 다운로드 폴더에 저장되었습니다.',
                                    icon: 'success',
                                    timer: 3000,
                                    showConfirmButton: false
                                });
                                return;
                            } else {
                                throw new Error(result.error || '다운로드 실패');
                            }
                        }
                    } catch (ipcError) {
                        console.warn('IPC 다운로드 실패, 직접 axios 사용:', ipcError);
                    }

                    // 방법 2: 직접 axios 사용 (fallback)
                    const directAxios = (await import('axios')).default;

                    response = await directAxios.get(
                        `${this.$apiUrls.hanqmedi}/downloadBeneficiaryExcelTemplate`,
                        {
                            responseType: 'blob',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                            },
                            timeout: 60000, // 60초로 증가
                            maxContentLength: 50 * 1024 * 1024, // 50MB 제한
                            validateStatus: (status) => status < 500 // 500 에러는 throw
                        }
                    );

                } else {
                    // 웹 환경
                    console.log('웹 환경에서 다운로드 처리');

                    const directAxios = (await import('axios')).default;

                    response = await directAxios.get(
                        `${this.$apiUrls.hanqmedi}/downloadBeneficiaryExcelTemplate`,
                        {
                            responseType: 'blob',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            timeout: 30000
                        }
                    );
                }

                loading.close();

                // 응답 데이터 검증
                if (!response.data || response.data.size === 0) {
                    throw new Error('다운로드된 파일이 비어있습니다.');
                }

                // 파일 다운로드 처리
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                // 파일명 설정 (한글 파일명 처리)
                const fileName = '고객 일괄등록 템플릿.xlsx';

                // 일렉트론 환경에서 다운로드 처리
                if (this.$env.isElectron) {
                    try {
                        // 일렉트론의 다운로드 API 사용
                        if (window.electronAPI && window.electronAPI.saveFile) {
                            const base64Data = await this.blobToBase64(blob);
                            const result = await window.electronAPI.saveFile(fileName, base64Data);

                            if (result.success) {
                                this.$swal.fire({
                                    title: '다운로드 완료',
                                    text: `파일이 저장되었습니다: ${result.filePath}`,
                                    icon: 'success',
                                    timer: 3000,
                                    showConfirmButton: false
                                });
                                return;
                            }
                        }
                    } catch (saveError) {
                        console.warn('일렉트론 저장 실패, 브라우저 다운로드 사용:', saveError);
                    }
                }

                // 브라우저 다운로드 (fallback)
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // 메모리 정리
                setTimeout(() => {
                    window.URL.revokeObjectURL(url);
                }, 1000);

                this.$swal.fire({
                    title: '다운로드 완료',
                    text: '엑셀 템플릿이 성공적으로 다운로드되었습니다.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

            } catch (error) {
                console.error('템플릿 다운로드 오류:', error);

                // 더 자세한 에러 정보 로깅
                if (error.response) {
                    console.error('서버 응답 상세:', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        headers: error.response.headers,
                        data: error.response.data
                    });
                }

                let errorMessage = '엑셀 템플릿 다운로드 중 오류가 발생했습니다.';

                if (error.response) {
                    switch (error.response.status) {
                        case 401:
                            errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
                            break;
                        case 403:
                            errorMessage = '템플릿 다운로드 권한이 없습니다.';
                            break;
                        case 404:
                            errorMessage = '템플릿 파일을 찾을 수 없습니다.';
                            break;
                        case 500:
                            errorMessage = '서버에서 템플릿 생성 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
                            break;
                        default:
                            errorMessage = `서버 오류 (${error.response.status}): ${error.response.statusText}`;
                    }
                } else if (error.code === 'ECONNABORTED') {
                    errorMessage = '다운로드 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    title: '다운로드 실패',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        uploadExcelFile() {
            // 파일 업로드 input 생성
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.csv,.xlsx,.xls';
            input.style.display = 'none';

            input.onchange = (event) => {
                const file = event.target.files[0];
                if (file) {
                    this.processExcelFile(file);
                }
            };

            document.body.appendChild(input);
            input.click();
            document.body.removeChild(input);
        },

        async processExcelFile(file) {
            try {
                // 파일 확장자 체크
                const fileExt = file.name.split('.').pop().toLowerCase();
                if (fileExt !== 'xlsx') {
                    this.$swal.fire('오류', 'Excel 파일(.xlsx)만 업로드 가능합니다.', 'error');
                    return;
                }

                // companykey 확인
                if (!this.$globalState.company_key) {
                    this.$swal.fire('오류', '회사 정보가 없습니다. 다시 로그인해주세요.', 'error');
                    return;
                }

                // 로딩 표시
                const loading = this.$swal.fire({
                    title: '고객 엑셀 업로드 중...',
                    text: '잠시만 기다려주세요.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                let response;

                // 일렉트론 환경에서 IPC 파일 업로드 사용
                if (this.$env.isElectron) {

                    // 파일을 base64로 변환
                    const base64Data = await this.fileToBase64(file);

                    // 데이터 구조 수정 - fileData를 직접 전달
                    const formData = {
                        fileName: file.name,
                        fileData: base64Data,
                        companykey: this.$globalState.company_key
                    };

                    // 토큰을 config로 전달
                    const config = {
                        token: localStorage.getItem('token')
                    };

                    // IPC를 통한 파일 업로드 - recipients 타입 사용
                    const uploadResult = await window.electronAPI.uploadFile('recipients', formData, config);

                    if (uploadResult.success) {
                        response = { data: uploadResult.data };
                    } else {
                        throw new Error(uploadResult.error || '파일 업로드 실패');
                    }

                } else {
                    // 웹 환경에서 FormData 사용
                    const formData = new FormData();
                    formData.append('recipients', file);
                    formData.append('companykey', this.$globalState.company_key);

                    const directAxios = (await import('axios')).default;

                    response = await directAxios.post(
                        `${this.$apiUrls.hanqmedi}/uploadRecipientsExcel`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            timeout: 300000,
                            maxContentLength: 100 * 1024 * 1024
                        }
                    );
                }

                loading.close();

                // 상세 결과 정보 구성
                const result = response.data.data;
                const totalRows = result.rowsProcessed + result.rowsFailed;

                let resultHtml = `
                    <div style="text-align: center; margin-bottom: 15px;">
                        <h4 style="color: #28a745; margin-bottom: 10px;">📊 고객 등록 결과</h4>
                        <p><strong>총 처리 건수:</strong> ${totalRows}건</p>
                        <p><strong>성공:</strong> <span style="color: #28a745;">${result.rowsProcessed}건</span></p>
                        <p><strong>실패:</strong> <span style="color: #dc3545;">${result.rowsFailed}건</span></p>
                    </div>
                `;

                // 실패한 행이 있는 경우 상세 정보 표시
                if (result.rowsFailed > 0 && result.failedRows && result.failedRows.length > 0) {
                    resultHtml += `
                        <div style="text-align: center; margin-top: 15px;">
                            <h5 style="color: #dc3545; margin-bottom: 10px;">❌ 실패한 행 상세정보</h5>
                            <div style="max-height: 200px; overflow-y: auto; background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
                    `;

                    result.failedRows.forEach(({ rowIndex, error, data }) => {
                        resultHtml += `
                            <div style="margin-bottom: 8px; padding: 5px; border-left: 3px solid #dc3545; background: white;">
                                <strong>${rowIndex}번 행:</strong> ${error}<br>
                                <small style="color: #6c757d;">데이터: ${JSON.stringify(data)}</small>
                            </div>
                        `;
                    });

                    resultHtml += `
                            </div>
                        </div>
                    `;
                }

                this.$swal.fire({
                    title: '고객 등록 완료',
                    html: resultHtml,
                    icon: 'success',
                    width: '600px',
                    confirmButtonText: '확인',
                    showCloseButton: true
                });

                // 목록 새로고침
                this.fetchData();

            } catch (error) {
                console.error('고객 엑셀 업로드 오류:', error);

                // 더 자세한 에러 정보 로깅
                if (error.response) {
                    console.error('서버 응답 상세:', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        data: error.response.data,
                        headers: error.response.headers
                    });
                }

                let errorMessage = '고객 엑셀 업로드 중 오류가 발생했습니다.';

                if (error.response) {
                    switch (error.response.status) {
                        case 400:
                            errorMessage = error.response.data.message || '잘못된 요청입니다.';
                            break;
                        case 401:
                            errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
                            break;
                        case 413:
                            errorMessage = '파일 크기가 너무 큽니다.';
                            break;
                        case 500:
                            errorMessage = error.response.data.message || '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
                            break;
                        default:
                            errorMessage = `서버 오류 (${error.response.status}): ${error.response.data.message || error.response.statusText}`;
                    }
                } else if (error.code === 'ECONNABORTED') {
                    errorMessage = '업로드 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    title: '업로드 실패',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 파일을 base64로 변환하는 메서드 추가
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },

        async fetchData() {
            if (this.isLoading) return;
            this.isLoading = true;

            try {
                // 전역 상태에서 id가 없는 경우 처리
                if (!this.$globalState.company_key) {
                    const userInfo = localStorage.getItem('userInfo');
                    if (userInfo) {
                        const parsedInfo = JSON.parse(userInfo);
                        this.$setGlobalState('id', parsedInfo.id);
                        this.$setGlobalState('manager_name', parsedInfo.name);
                        this.$setGlobalState('company_name', parsedInfo.company);
                        this.$setGlobalState('company_key', parsedInfo.company_key);
                    } else {
                        this.$swal.fire('인증 오류', '로그인이 필요합니다.', 'error');
                        this.$router.push('/login');
                        return;
                    }
                }

                if (!this.$globalState.company_key) {
                    this.$swal.fire('인증 오류', '사업소 정보를 찾을 수 없습니다.', 'error');
                    this.$router.push('/login');
                    return;
                }

                this.searchSubmitted = this.searchQuery;

                // 백엔드 API를 통해 고객 정보 조회 (급여 + 비급여)
                const apiUrl = `${this.$apiUrls.hanqmedi}/getClients/${this.$globalState.company_key}`;

                const response = await this.$axios({
                    method: 'get',
                    url: apiUrl,
                    params: {
                        page: this.currentPage,
                        limit: this.itemsPerPage,
                        search: this.searchSubmitted,
                        target: this.selectedBenefitType !== 'all' ? this.selectedBenefitType : '',
                        type: this.selectedTypeFilter !== '전체' ? this.selectedTypeFilter : '',
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                // API 응답 구조에 맞게 수정
                if (response.data && response.data.recipients && Array.isArray(response.data.recipients)) {
                    // API 응답 데이터를 프론트엔드에서 사용하는 형태로 매핑
                    this.clients_info.clients = response.data.recipients.map(client => ({
                        name: client.name || '',
                        number: client.number || '',
                        cnum: client.cnum || '',
                        target: client.target || '',
                        type: client.type || '급여',
                        customer_type: client.customer_type || '일반',
                        address: client.address || '',
                        address_detail: client.address_detail || '',
                        address2: client.address2 || '',
                        address_detail2: client.address_detail2 || '',
                        guardname: client.guard_name || '',
                        guardtarget: client.guard_target || '',
                        manager: client.manager || '',
                        phone1: client.phone1 || '',
                        phone2: client.phone2 || '',
                        // 추가 필드들도 매핑
                        id: client.id,
                        company_id: client.company_id,
                        user_id: client.user_id,
                        address2: client.address2 || '',
                        address_detail: client.address_detail || client.address2 || '',
                        address_num: client.address_num || '',
                        bank_accout: client.bank_accout || '',
                        bank_admin: client.bank_admin || '',
                        bank_name: client.bank_name || '',
                        bank_phone: client.bank_phone || '',
                        bank_resident: client.bank_resident || '',
                        center_name: client.center_name || '',
                        cnum: client.cnum || '',
                        gender: client.gender || '',
                        guard_resident: client.guard_resident || '',
                        memo: client.memo || '',
                        msgno: client.msgno || '',
                        ranker: client.ranker || '',
                        rcgt: client.rcgt || '',
                        sale: client.sale || 0,
                        created_at: client.created_at || '',
                        apdt_s: client.apdt_s || '',
                        apdt_e: client.apdt_e || '',
                        chk: client.chk || '',
                        // 출처 구분 정보 추가
                        source: client.source || 'unknown',
                        isNonBenefit: client.isNonBenefit || false
                    }));

                    // total_count로 총 개수 가져오기
                    this.clients_info.total_all_cnt = response.data.total_count || 0;

                    // 통계 계산 (target 필드 기반 - 급여 고객)
                    const stats = {
                        general_cnt: 0,
                        basic_cnt: 0,
                        discount_cnt: 0,
                        medical_cnt: 0,
                        non_benefit_cnt: 0,
                        // 비급여 고객 customer_type별 통계
                        non_benefit_general_cnt: 0,
                        non_benefit_oxygen_cnt: 0,
                        non_benefit_diabetes_cnt: 0,
                        non_benefit_assistive_device_cnt: 0
                    };

                    response.data.recipients.forEach(client => {
                        if (client.isNonBenefit || client.type === '비급여') {
                            // 비급여 고객 - customer_type별 카운팅
                            stats.non_benefit_cnt++;
                            switch (client.customer_type) {
                                case '일반':
                                    stats.non_benefit_general_cnt++;
                                    break;
                                case '산소':
                                    stats.non_benefit_oxygen_cnt++;
                                    break;
                                case '당뇨':
                                    stats.non_benefit_diabetes_cnt++;
                                    break;
                                case '보장구':
                                    stats.non_benefit_assistive_device_cnt++;
                                    break;
                                default:
                                    // customer_type이 없는 경우 기본값으로 '일반' 처리
                                    stats.non_benefit_general_cnt++;
                                    break;
                            }
                        } else {
                            // 급여 고객 - target별 카운팅
                            switch (client.target) {
                                case '일반':
                                    stats.general_cnt++;
                                    break;
                                case '기초':
                                    stats.basic_cnt++;
                                    break;
                                case '감경':
                                    stats.discount_cnt++;
                                    break;
                                case '의료급여':
                                    stats.medical_cnt++;
                                    break;
                            }
                        }
                    });

                    // 통계 데이터 저장
                    this.clients_info.general_cnt = stats.general_cnt;
                    this.clients_info.basic_cnt = stats.basic_cnt;
                    this.clients_info.discount_cnt = stats.discount_cnt;
                    this.clients_info.medical_cnt = stats.medical_cnt;
                    this.clients_info.non_benefit_cnt = stats.non_benefit_cnt;
                    this.clients_info.non_benefit_general_cnt = stats.non_benefit_general_cnt;
                    this.clients_info.non_benefit_oxygen_cnt = stats.non_benefit_oxygen_cnt;
                    this.clients_info.non_benefit_diabetes_cnt = stats.non_benefit_diabetes_cnt;
                    this.clients_info.non_benefit_assistive_device_cnt = stats.non_benefit_assistive_device_cnt;

                    // 필터링된 검색 결과가 없을 때 알림
                    if ((this.searchSubmitted || this.selectedBenefitType !== 'all') && this.clients_info.total_all_cnt === 0) {
                        let conditions = [];
                        if (this.searchSubmitted) conditions.push(`검색어: "${this.searchSubmitted}"`);
                        if (this.selectedBenefitType !== 'all') conditions.push(`대상: ${this.getBenefitTypeLabel(this.selectedBenefitType)}`);

                        this.$swal.fire({
                            title: '검색 결과 없음',
                            text: `${conditions.join(', ')} 조건에 맞는 고객이 없습니다.`,
                            icon: 'info',
                            confirmButtonText: '확인'
                        });
                    }

                    this.saveState();
                } else {
                    this.clients_info.clients = [];
                    this.clients_info.total_all_cnt = 0;
                    this.clients_info.general_cnt = 0;
                    this.clients_info.basic_cnt = 0;
                    this.clients_info.discount_cnt = 0;
                    this.clients_info.medical_cnt = 0;
                    this.clients_info.non_benefit_cnt = 0;
                    this.clients_info.non_benefit_general_cnt = 0;
                    this.clients_info.non_benefit_oxygen_cnt = 0;
                    this.clients_info.non_benefit_diabetes_cnt = 0;
                    this.clients_info.non_benefit_assistive_device_cnt = 0;
                }
            } catch (error) {
                console.error('데이터를 가져오는 중 오류 발생:', error);
                this.$swal.fire('오류', '데이터를 불러오는데 실패했습니다.', 'error');
                this.clients_info.clients = [];
                this.clients_info.total_all_cnt = 0;
                this.clients_info.general_cnt = 0;
                this.clients_info.basic_cnt = 0;
                this.clients_info.discount_cnt = 0;
                this.clients_info.medical_cnt = 0;
                this.clients_info.non_benefit_cnt = 0;
                this.clients_info.non_benefit_general_cnt = 0;
                this.clients_info.non_benefit_oxygen_cnt = 0;
                this.clients_info.non_benefit_diabetes_cnt = 0;
                this.clients_info.non_benefit_assistive_device_cnt = 0;
            } finally {
                this.isLoading = false;
            }
        },

        // 상태 저장/복원
        saveState() {
            const state = {
                searchQuery: this.searchQuery,
                searchSubmitted: this.searchSubmitted,
                selectedBenefitType: this.selectedBenefitType,
                currentPage: this.currentPage,
                itemsPerPage: this.itemsPerPage,
                lastVisit: new Date().getTime()
            };

            localStorage.setItem('clientManageState', JSON.stringify(state));
        },

        restoreState() {
            const isRefresh = sessionStorage.getItem('clientManageVisited') === 'true';

            if (isRefresh) {
                console.log('새로고침 감지: 상태 초기화');
                localStorage.removeItem('clientManageState');
                sessionStorage.removeItem('clientManageVisited');
                return;
            }

            const savedState = localStorage.getItem('clientManageState');
            if (!savedState) return;

            try {
                const state = JSON.parse(savedState);
                this.searchQuery = state.searchQuery || '';
                this.searchSubmitted = state.searchSubmitted || '';
                this.selectedBenefitType = state.selectedBenefitType || 'all';
                this.currentPage = state.currentPage || 1;
                this.itemsPerPage = state.itemsPerPage || 20;
            } catch (error) {
                console.error('상태 복원 중 오류 발생:', error);
                localStorage.removeItem('clientManageState');
            }
        },

        // 급여/비급여 필터 변경
        changeTypeFilter() {
            this.currentPage = 1; // 첫 페이지로 이동
            this.fetchData();
        },

        // 검색 및 필터링
        searchHandler() {
            this.currentPage = 1;
            this.fetchData();
            this.saveState();
        },

        filterByBenefitType(type) {
            this.selectedBenefitType = type;
            this.currentPage = 1;  // 첫 페이지로 이동
            this.fetchData();
            this.saveState();
        },

        // 필터 초기화
        async clearFilter(filterType) {
            switch (filterType) {
                case 'benefitType':
                    this.selectedBenefitType = 'all';
                    break;
            }
            this.currentPage = 1;
            await this.fetchData();
            this.saveState();
        },

        async clearAllFilters() {
            this.selectedBenefitType = 'all';
            this.searchQuery = '';
            this.searchSubmitted = '';
            this.currentPage = 1;
            await this.fetchData();
            this.saveState();
        },

        // 페이지네이션
        async goToPage(page) {
            if (page < 1 || page > this.totalPages || page === this.currentPage) {
                return;
            }
            this.currentPage = page;
            await this.fetchData();
            this.saveState();
        },

        async goToPageGroup(direction) {
            let newPage;
            const currentGroup = Math.floor((this.currentPage - 1) / 10);

            if (direction === 'prev') {
                newPage = Math.max(1, (currentGroup - 1) * 10 + 1);
            } else {
                newPage = Math.min(this.totalPages, (currentGroup + 1) * 10 + 1);
            }

            if (newPage !== this.currentPage) {
                this.currentPage = newPage;
                await this.fetchData();
                this.saveState();
            }
        },

        changeItemsPerPage() {
            this.currentPage = 1;
            this.fetchData();
            this.saveState();
        },

        // 유틸리티 메서드들
        getBenefitTypeLabel(type) {
            const typeObj = this.benefitTypes.find(t => t.value === type);
            return typeObj ? typeObj.label : type;
        },
    },

    async mounted() {
        this.restoreState();
        await this.fetchData();
        sessionStorage.setItem('clientManageVisited', 'true');
    },

    activated() {
        sessionStorage.setItem('clientManageVisited', 'true');
    },

    deactivated() {
        this.saveState();
    },

    beforeUnmount() {
        this.saveState();
        sessionStorage.removeItem('clientManageVisited');
    },

    watch: {
        // 검색어 변경 시 자동 검색
        searchQuery() {
            this.saveState();
        },

        selectedBenefitType() {
            this.saveState();
        }
    }
}
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>